import { Suspense, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { updatePageState } from "../../../../../data/store/pageSlice"
import { ACL_IDENTIFIERS, APP_PAGES } from "../../../../../data/configs/constants"
import {
  LiveChatEnabled,
  getLiveChatConfigurations,
  getMembers,
  updateLiveChatConfigurations,
  updateTenantInfo
} from "../../../../../data/store/overviewSlice"
import OperationalHourSettings from "../../../../components/OperationalHourSettings"
import AclComponentWrapper from "../../../../components/AclComponentWrapper"
import ErrorBoundary from "../../../../components/ErrorBoundary"
import LiveChatDisabled from "../../LiveChats/LiveChatDisabled"
import SpinnerLoader from "../../../../components/SpinnerLoader"
import { updateLiveChatState } from "../../../../../data/store/liveChatSlice"
import liveChatInitialState from "../../../../../data/store/liveChatSlice/initialState"
import { getDenyList, updateDenyList } from "../../../../../data/store/templateSlice";

import { Row, Col, Typography, Switch, Card, Alert, Modal, Input, Button, Space, Tag, notification } from 'antd';
import Notify, { notify } from "../../../../components/Notify"

const { Title, Text } = Typography;

const LIVE_CHAT_PSEUDO_NAME = "liveChat"

const HumanAssistSettings = () => {
  const { tenantId } = useParams()
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const tenantInfo = useSelector((state) => state.overviewDetails.tenantInfo)
  const currentPageAcl = useSelector((state) => state.pageDetails?.currentPageAcl);
  const liveChatConfigurations = useSelector(
    (state) => state.overviewDetails.liveChatConfigurations
  )
  const isLiveChatsEnabled = useSelector(
    (state) =>
      state.overviewDetails?.tenantInfo?.featureControl?.liveChat?.active
  )
  const [negationInputText, setNegationInputText] = useState("")
  const denyList = useSelector((state) => state.templateDetails.denyList)
  const negationList = denyList?.[LIVE_CHAT_PSEUDO_NAME]?.denyList || []

  useEffect(() => {
    dispatch(
      updatePageState({
        currentPage: APP_PAGES.TENANT_HUMAN_ASSIST_SETTINGS,
        currentSiderModule: APP_PAGES.TENANT_HUMAN_ASSIST_SETTINGS,
        showTenantHeader: true,
      })
    )
  }, [dispatch])

  useEffect(() => {
    dispatch(
      getMembers({
        tenantId,
      })
    )
    dispatch(getLiveChatConfigurations({ tenantId }))
  }, [dispatch, tenantId])

  const handleUpdateOperationalHour = (changedValues) => {
    const payload = {
      ...tenantInfo,
      ...changedValues,
    }
    dispatch(updateTenantInfo(payload)).then((res) => notify.success({message: "Operational Hours settings updated!"})).catch((err)=> notify.error({message: "An error occured!"}))
  }

  const handleEmailNotification = (checked) => {
    dispatch(
      updateLiveChatConfigurations({
        tenantId,
        enableEmailNotify: checked,
      })
    )
      .unwrap()
      .then((res) => {
        notify.success({ message: res?.message ? res?.message : "Updated Agent Email notification" })
      });
  };


  const handleUserConfirmation = (checked) => {
    const payload = {
      ...tenantInfo,
      userConfirmationNeeded: checked,
    }
    dispatch(updateTenantInfo(payload))
  }

  const handleLiveChatOnlySwitch = (checked) => {
    dispatch(updateTenantInfo({
      ...tenantInfo,
      liveChatOnly: checked
    }))
      .unwrap()
      .then((res) => {
        notify.success({ message: res?.message ? res?.message : "Tenant Live Chat Settings Updated" })
      })
  }

  const handleLiveChats = () => {
    dispatch(
      LiveChatEnabled({
        tenantId,
        status: false,
      })
    )
      .unwrap()
      .then(() => {
        dispatch(updateLiveChatState(liveChatInitialState))
      })
  }

  const updateNegationList = (newList) => {
    dispatch(updateDenyList({
      tenantId: tenantId,
      feature: LIVE_CHAT_PSEUDO_NAME,
      payload: {
        ...denyList[LIVE_CHAT_PSEUDO_NAME],
        denyList: newList
      }
    }
    )
    )
  }

  useEffect(() => {
    dispatch(getDenyList({
      feature: LIVE_CHAT_PSEUDO_NAME,
      tenantId: tenantId
    }))
  }, [dispatch, tenantId]);

  if (!isLiveChatsEnabled) return (
    <ErrorBoundary>
      <Suspense fallback={<SpinnerLoader />}>
        <LiveChatDisabled
          tenantId={tenantInfo.tenantId}
          active={isLiveChatsEnabled}
        />
      </Suspense>
    </ErrorBoundary>
  )

  return (
    <div style={{ padding: 7 }}>
      <Notify />
      <Row gutter={[16, 16]} justify="space-between">
        <Col span={24}>
          <AclComponentWrapper payWallSize="small" componentAcl={currentPageAcl?.[ACL_IDENTIFIERS.LIVE_CHAT_CONFIGURATION]}>
            <Title level={3} style={{ margin: 0, marginBottom: 13 }}>Live Chat Configuration</Title>

            <OperationalHourSettings
              data={tenantInfo?.featureControl?.liveChat?.operationalHourSettings}
              onChange={handleUpdateOperationalHour}
            />

            <Card style={{ marginBottom: 16 }} bordered={false}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Row justify="space-between" align="middle">
                  <Text strong>Ask for user confirmation</Text>
                  <Switch
                    size="small"
                    checked={tenantInfo?.featureControl?.liveChat?.userConfirmationNeeded || false}
                    onChange={handleUserConfirmation}
                  />
                </Row>
                <Text type="secondary">
                  Note: If this mode is enabled, your smart chat-bot will ask the user for confirmation before transferring to a live representative.
                </Text>
              </Space>
            </Card>

            {liveChatConfigurations?.enableEmailNotify !== undefined && (
              <Card style={{ marginBottom: 16 }} bordered={false}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Row justify="space-between" align="middle">
                    <Text strong>Email Notification</Text>
                    <Switch
                      size="small"
                      checked={liveChatConfigurations?.enableEmailNotify}
                      onChange={handleEmailNotification}
                    />
                  </Row>
                  <Text type="secondary">
                    Note: Agent can change getting email notification during any busy state.
                  </Text>
                </Space>
              </Card>
            )}
          </AclComponentWrapper>

          <Card style={{ marginBottom: 16 }} bordered={false}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>Ignore list</Text>
              <Text type="secondary">Add messages to ignore from live chat transfer triggers.</Text>

              <Input
                size="large"
                placeholder="Enter message to negate"
                value={negationInputText}
                onChange={(e) => setNegationInputText(e.target.value)}
                onPressEnter={() => {
                  updateNegationList([...negationList, negationInputText]);
                  setNegationInputText('');
                }}
                suffix={
                  <Button
                    size="small"
                    icon={<i className="ri-add-line" />}
                    onClick={() => {
                      updateNegationList([...negationList, negationInputText]);
                      setNegationInputText('');
                    }}
                  />
                }
              />

              {negationList.length > 0 ? (
                <Row gutter={[8, 8]}>
                  {negationList.map((negation, index) => (
                    <Col key={index}>
                      <Tag
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          const updatedList = [...negationList];
                          updatedList.splice(index, 1);
                          updateNegationList(updatedList);
                        }}
                      >
                        {negation}
                      </Tag>
                    </Col>
                  ))}
                </Row>
              ) : (
                <Text type="secondary" style={{ textAlign: 'center' }}>
                  No messages added
                </Text>
              )}
            </Space>
          </Card>

          <Alert
            message="Disable AI"
            description="Note: Please be aware that by taking this action, all AI responses will be disabled, and the completion of chats will require the assistance of live agents."
            type="warning"
            showIcon={false}
            action={
              <Switch
                size="small"
                checked={tenantInfo?.featureControl?.liveChat?.liveChatOnly || false}
                onChange={handleLiveChatOnlySwitch}
              />
            }
            style={{ marginBottom: 16 }}
          />

          <Alert
            message="Disable live chat"
            description={
              <Text type="secondary">
                Note: Please be aware that by taking this action, all live chat functionality will be disabled. You can re-enable it at any time.
              </Text>
            }
            type="error"
            showIcon={false}
            action={
              <Button
                size="small"
                danger
                onClick={() => {
                  setOpen(true);
                }}
              >
                Disable
              </Button>
            }
          />

          <Modal
            title="Disable live chat"
            open={open}
            onCancel={() => setOpen(false)}
            footer={[
              <Button key="cancel" onClick={() => setOpen(false)} danger>
                Cancel
              </Button>,
              <Button
                key="submit"
                type="primary"
                onClick={() => {
                  handleLiveChats();
                  setOpen(false);
                }}
              >
                Disable
              </Button>,
            ]}
          >
            <Text>Are you sure you want to disable live chat on your bot?</Text>
          </Modal>
        </Col>
      </Row>
    </div>
  )
}

export default HumanAssistSettings
